import React, { useState } from 'react';
import { ChatPipelineReviewSampling, ChatPipelineRoutingDestination } from '@scale/shared-types';
import { Icon, SelectMenu } from '@scale/scaleui';
import ArrayBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/ArrayBuilder';
import { ChatPipelineReviewSamplingSchema } from '@scale/shared/pipelinev3/genai';
import type { TypeBuilderMapType } from 'react-scale/corp/components/QM/taskTemplatesEditor/BuilderSchemasType';

interface ReviewSamplingBuilderProps {
  name: string;
  existingValue?: Array<ChatPipelineReviewSampling>;
  updateProperty: (arg: Array<ChatPipelineReviewSampling>) => void;
  required?: boolean;
  taskTemplateParams?: Record<string, any>;
  typeBuilderMap: TypeBuilderMapType;
}

interface ReviewLevelOption {
  id: ChatPipelineRoutingDestination;
  name: string;
}

const reviewLevelOptions: Array<ReviewLevelOption> = [
  {
    id: ChatPipelineRoutingDestination.Attempt,
    name: 'attempt',
  },
  {
    id: ChatPipelineRoutingDestination.L0,
    name: 'l0',
  },
  {
    id: ChatPipelineRoutingDestination.L1,
    name: 'l1',
  },
  {
    id: ChatPipelineRoutingDestination.L4,
    name: 'l4',
  },
  {
    id: ChatPipelineRoutingDestination.L10,
    name: 'l10',
  },
];

const ReviewSamplingBuilder: React.FC<ReviewSamplingBuilderProps> = ({
  name,
  existingValue = [],
  updateProperty,
  taskTemplateParams,
  typeBuilderMap,
}) => {
  const reviewSampling = existingValue;
  const [selectedReviewLevel, setSelectedReviewLevel] = useState<ChatPipelineRoutingDestination>(
    ChatPipelineRoutingDestination.Attempt,
  );
  const addReviewLevel = (reviewLevel: ChatPipelineRoutingDestination) => {
    const newReviewSampling: ChatPipelineReviewSampling[] = [...reviewSampling];
    // Add a new review sampling config to the array with default values in the editor
    newReviewSampling.push({
      currentReviewLevel: reviewLevel,
      minSamples: 5,
      minScore: 0.7,
      reviewSamplingPercentage: 0.1,
    } as any);
    updateProperty(newReviewSampling);
  };

  const getKey = (reviewSample: ChatPipelineReviewSampling) => {
    return (
      reviewSample.currentReviewLevel +
      ' ' +
      reviewSample.overrideDestinationReviewLevel +
      ' ' +
      reviewSample.minScore
    );
  };

  const getTitle = (reviewSample: ChatPipelineReviewSampling) => {
    return (
      'Review Sampling for ' +
      reviewSample.currentReviewLevel +
      ' with override destination ' +
      reviewSample.overrideDestinationReviewLevel
    );
  };

  return (
    <div className="scaleui scaleui-tailwind scale-theme">
      {name && <div className="my-2 text-lg">{name}</div>}
      <div className="prose text-sm italic mb-4">
        Configure the review sampling for this task. Review sampling allows you to specify the
        percentage of tasks that get a review at each review level based on the accuracy of the
        contributor. Select the source review level first when adding.
      </div>
      <ArrayBuilder
        typeBuilderMap={typeBuilderMap}
        elements={reviewSampling}
        setElements={updateProperty}
        getKey={reviewSample => getKey(reviewSample)}
        getTitle={reviewSample => getTitle(reviewSample)}
        getSchema={_ => ChatPipelineReviewSamplingSchema}
        fullWidth
        taskTemplateParams={taskTemplateParams}
      />
      <div className="flex">
        <SelectMenu
          className="w-[80%]"
          options={reviewLevelOptions}
          selectedOptionId={selectedReviewLevel}
          setSelected={value => {
            setSelectedReviewLevel(value);
          }}
        />
        <Icon
          className="ml-4"
          hasBackground
          icon={['fas', 'plus']}
          onClick={() => addReviewLevel(selectedReviewLevel)}
          size="lg"
          variant="primary"
        />
      </div>
    </div>
  );
};

export default ReviewSamplingBuilder;
